import {ChangeDetectorRef, Injectable, signal} from '@angular/core';
import {BehaviorSubject, filter, NEVER, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../destroyable';
import {DynRemote} from './dyn-xyz-wc/dyn-remote';

@Injectable()
export class DynamicZyxDaemonService {

    public dynRemote: DynRemote;

    public readonly $dynRemote = signal<DynRemote | undefined>(undefined);

    public readonly dynRemote$: Observable<DynRemote | undefined>;

    public readonly $isLoggedIn = signal<boolean | undefined>(undefined);

    private readonly dynRemoteSubject$ = new BehaviorSubject<DynRemote | undefined>(undefined);

    private readonly destroy$ = destroy$();

    private dynRemoteNestedSubscription: Subscription;

    constructor() {
        this.dynRemote$ = this.dynRemoteSubject$.asObservable();
    }

    public setDynRemote(dynRemote: DynRemote): void {
        (window as any).dynRemote = dynRemote; // TODO

        this.dynRemote = dynRemote;
        this.$dynRemote.set(dynRemote);
        this.dynRemoteSubject$.next(dynRemote);

        this.dynRemoteNestedSubscription?.unsubscribe();
        this.dynRemoteNestedSubscription = undefined;

        if (!dynRemote) {
            this.$isLoggedIn.set(undefined);
        } else {
            this.dynRemoteNestedSubscription = dynRemote.isLoggedIn$.pipe(
                tap(isLoggedIn => this.$isLoggedIn.set(isLoggedIn)),
                takeUntil(this.destroy$)
            ).subscribe();
        }
    }

    public listenToDynRemote(
        destroy$: Observable<void>,
        changeDetectionRef?: ChangeDetectorRef,
        callback?: (dynRemote: DynRemote) => void
    ): BehaviorSubject<DynRemote | undefined> {
        const resultSubject$ = new BehaviorSubject<DynRemote | undefined>(this.dynRemote);

        this.dynRemote$.pipe(
            tap(dynRemote => {
                resultSubject$.next(dynRemote);
                callback?.(dynRemote);
                changeDetectionRef?.markForCheck();
            }),
            takeUntil(destroy$)
        ).subscribe();

        return resultSubject$;
    }

    public getDynAuthToken(): string | undefined {
        return this.dynRemote.getMinAuthToken();
    }

    public listenToAuthToken(): Observable<string | undefined> {
        return this.getIsLoggedInStream().pipe(
            map(isLoggedIn =>
                isLoggedIn
                    ? this.getDynAuthToken()
                    : undefined
            ),
            distinctUntilChanged()
        );
    }

    public getIsLoggedInStream(): Observable<boolean> {
        return this.dynRemote$.pipe(
            switchMap(dynRemote =>
                dynRemote
                    ? dynRemote.isLoggedIn$
                    : NEVER
            ),
            distinctUntilChanged()
        );
    }

    public getLogInStream(): Observable<void> {
        return this.getIsLoggedInStream().pipe(
            filter(isLoggedIn => !!isLoggedIn),
            map((): void => undefined)
        );
    }

    public getLogOutStream(): Observable<void> {
        return this.getIsLoggedInStream().pipe(
            filter(isLoggedIn => !isLoggedIn),
            map((): void => undefined)
        );
    }

}
